import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Calendar, Image as ImageIcon, AlertCircle, Newspaper, Package, Compass, Lock, LogOut, Video, Clock, Save, Check } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import ProductsAdmin from '../components/ProductsAdmin';
import DecouvrirAdmin from '../components/DecouvrirAdmin';
import { API } from '../utils/api';

// Mot de passe admin (hashé en base64 pour éviter qu'il soit visible en clair dans le code)
const ADMIN_PASSWORD_HASH = 'R0BicmllMkxvZXVmcw==';

const Admin = () => {
  const { toast } = useToast();
  
  // État d'authentification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [activeTab, setActiveTab] = useState('actualites');
  const [actualites, setActualites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // État pour les horaires
  const [hoursData, setHoursData] = useState([
    { day: "Lundi", isOpen: false, open: "", close: "", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Mardi", isOpen: true, open: "09:00", close: "18:30", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Mercredi", isOpen: true, open: "09:00", close: "18:30", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Jeudi", isOpen: true, open: "09:00", close: "18:30", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Vendredi", isOpen: true, open: "09:00", close: "18:30", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Samedi", isOpen: true, open: "09:00", close: "17:30", hasBreak: false, breakStart: "", breakEnd: "" },
    { day: "Dimanche", isOpen: false, open: "", close: "", hasBreak: false, breakStart: "", breakEnd: "" }
  ]);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [alertActive, setAlertActive] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  
  // État pour les contacts
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingItem, setViewingItem] = useState(null);
  const [editingActualite, setEditingActualite] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    images: [],
    video: '',
    link_url: '',
    link_text: '',
    date: '',
    category: 'info',
    isNew: false
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);

  // Vérifier si déjà authentifié au chargement
  useEffect(() => {
    const authToken = sessionStorage.getItem('admin_auth');
    if (authToken === ADMIN_PASSWORD_HASH) {
      setIsAuthenticated(true);
    }
  }, []);

  // Fonction de connexion
  const handleLogin = (e) => {
    e.preventDefault();
    const inputHash = btoa(passwordInput);
    
    if (inputHash === ADMIN_PASSWORD_HASH) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_auth', ADMIN_PASSWORD_HASH);
      setLoginError('');
      setPasswordInput('');
      toast({
        title: "Connexion réussie",
        description: "Bienvenue dans l'administration",
      });
    } else {
      setLoginError('Mot de passe incorrect');
      setPasswordInput('');
    }
  };

  // Fonction de déconnexion
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    toast({
      title: "Déconnexion",
      description: "Vous avez été déconnecté",
    });
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchActualites();
      fetchHours();
      fetchContacts();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'contacts') {
      fetchContacts();
    }
  }, [activeTab]);

  const fetchHours = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.hours && response.data.hours.length > 0) {
        setHoursData(response.data.hours);
      }
      setAlertActive(response.data.alert_active || false);
      setAlertMessage(response.data.alert_message || '');
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await axios.get(`${API}/contact`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/contact/${id}/read`);
      setContacts(prev => prev.map(c => c.id === id ? { ...c, read: true } : c));
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de marquer comme lu", variant: "destructive" });
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await axios.delete(`${API}/contact/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast({ title: "Succès", description: "Message supprimé" });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer", variant: "destructive" });
    }
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      await axios.put(`${API}/settings`, {
        hours: hoursData,
        alert_active: alertActive,
        alert_message: alertMessage
      });
      setHoursSaved(true);
      toast({ title: "Succès", description: "Paramètres mis à jour avec succès" });
      setTimeout(() => setHoursSaved(false), 3000);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    } finally {
      setSavingHours(false);
    }
  };

  const updateDayHours = (index, field, value) => {
    setHoursData(prev => prev.map((d, i) => i === index ? { ...d, [field]: value } : d));
  };

  const fetchActualites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/actualites`);
      setActualites(response.data);
    } catch (error) {
      console.error('Error fetching actualites:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les actualités",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Utilisez JPG, PNG, GIF ou WEBP",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (10MB max for Cloudinary)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximum est de 10MB",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Use functional form to avoid closure issues
      setFormData(prev => ({ ...prev, image: response.data.url }));
      setErrors(prev => ({ ...prev, image: '' })); // Clear image error
      
      toast({
        title: "✅ Succès",
        description: "Image uploadée avec succès ! Vous pouvez maintenant remplir les autres champs."
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "❌ Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader l'image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleFilesSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Max 10 images
    if (files.length > 10) {
      toast({
        title: "Trop d'images",
        description: "Vous pouvez uploader maximum 10 images",
        variant: "destructive"
      });
      return;
    }

    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Type de fichier non autorisé",
          description: `${file.name}: Utilisez JPG, PNG, GIF ou WEBP`,
          variant: "destructive"
        });
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: "Fichier trop volumineux",
          description: `${file.name}: Maximum 10MB`,
          variant: "destructive"
        });
        return;
      }
    }

    setSelectedFiles(files);
    setUploadingMultiple(true);

    try {
      const formDataUpload = new FormData();
      files.forEach(file => {
        formDataUpload.append('files', file);
      });

      const response = await axios.post(`${API}/upload-multiple`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData(prev => ({ ...prev, images: [...prev.images, ...response.data.urls] }));
      
      toast({
        title: "✅ Succès",
        description: `${response.data.count} image(s) uploadée(s) !`
      });
    } catch (error) {
      console.error('Error uploading multiple files:', error);
      toast({
        title: "❌ Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader les images",
        variant: "destructive"
      });
    } finally {
      setUploadingMultiple(false);
    }
  };

  const removeGalleryImage = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, index) => index !== indexToRemove)
    }));
  };

  // Upload vidéo
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Utilisez MP4, WebM, MOV, AVI ou MPEG",
        variant: "destructive"
      });
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximum est de 100MB",
        variant: "destructive"
      });
      return;
    }

    setUploadingVideo(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-video`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload vidéo: ${percentCompleted}%`);
        }
      });

      setFormData(prev => ({ ...prev, video: response.data.url }));
      
      toast({
        title: "✅ Succès",
        description: "Vidéo uploadée avec succès !"
      });
    } catch (error) {
      console.error('Error uploading video:', error);
      toast({
        title: "❌ Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader la vidéo",
        variant: "destructive"
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Le titre est requis";
    if (!formData.description.trim()) newErrors.description = "La description est requise";
    // Image OU vidéo requis (pas les deux obligatoirement)
    if (!formData.image.trim() && !formData.video.trim()) {
      newErrors.media = "Veuillez ajouter au moins une image ou une vidéo";
    }
    if (!formData.date) newErrors.date = "La date est requise";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez remplir tous les champs requis",
        variant: "destructive"
      });
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        date: new Date(formData.date).toISOString()
      };

      if (editingActualite) {
        await axios.put(`${API}/actualites/${editingActualite.id}`, dataToSend);
        toast({
          title: "Succès",
          description: "Actualité mise à jour avec succès"
        });
      } else {
        await axios.post(`${API}/actualites`, dataToSend);
        toast({
          title: "Succès",
          description: "Actualité créée avec succès"
        });
      }

      fetchActualites();
      closeModal();
    } catch (error) {
      console.error('Error saving actualite:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder l'actualité",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette actualité ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/actualites/${id}`);
      toast({
        title: "Succès",
        description: "Actualité supprimée avec succès"
      });
      fetchActualites();
    } catch (error) {
      console.error('Error deleting actualite:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'actualité",
        variant: "destructive"
      });
    }
  };

  const openModal = (actualite = null) => {
    if (actualite) {
      setEditingActualite(actualite);
      setFormData({
        title: actualite.title,
        description: actualite.description,
        image: actualite.image,
        images: actualite.images || [],
        video: actualite.video || '',
        link_url: actualite.link_url || '',
        link_text: actualite.link_text || '',
        date: new Date(actualite.date).toISOString().split('T')[0],
        category: actualite.category,
        isNew: actualite.isNew
      });
    } else {
      setEditingActualite(null);
      setFormData({
        title: '',
        description: '',
        image: '',
        images: [],
        video: '',
        link_url: '',
        link_text: '',
        date: new Date().toISOString().split('T')[0],
        category: 'info',
        isNew: false
      });
    }
    setErrors({});
    setSelectedFiles([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingActualite(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      images: [],
      video: '',
      link_url: '',
      link_text: '',
      date: '',
      category: 'info',
      isNew: false
    });
    setErrors({});
    setSelectedFiles([]);
    setSelectedFile(null);
    setUploading(false);
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'nouveaute':
        return { label: '✨ Nouveauté', class: 'bg-green-100 text-green-700' };
      case 'info':
        return { label: '📢 Info', class: 'bg-blue-100 text-blue-700' };
      case 'promotion':
        return { label: '🎉 Promotion', class: 'bg-orange-100 text-orange-700' };
      default:
        return { label: '📢 Info', class: 'bg-blue-100 text-blue-700' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Écran de connexion */}
      {!isAuthenticated ? (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-green-100">
          <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full mx-4">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lock className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">🍝 IL TRULLO</h1>
              <p className="text-gray-600">Administration</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Mot de passe
                </label>
                <input
                  id="password"
                  type="password"
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                  placeholder="Entrez le mot de passe"
                  autoFocus
                />
                {loginError && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {loginError}
                  </p>
                )}
              </div>
              
              <button
                type="submit"
                className="w-full py-3 px-4 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <Lock className="w-5 h-5" />
                Se connecter
              </button>
            </form>
            
            <div className="mt-6 text-center">
              <a href="/" className="text-sm text-gray-500 hover:text-green-600 transition-colors">
                ← Retour au site
              </a>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Admin Header */}
          <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h1 className="text-2xl font-bold text-gray-900">🍝 IL TRULLO</h1>
                <span className="text-sm text-gray-500 border-l pl-4">Administration</span>
              </div>
              <div className="flex items-center gap-4">
                <a 
                  href="/" 
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-2"
                >
                  ← Retour au site
                </a>
                <button
                  onClick={handleLogout}
                  className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-2 border-l pl-4"
                >
                  <LogOut className="w-4 h-4" />
                  Déconnexion
                </button>
              </div>
            </div>
          </header>

      <div className="max-w-7xl mx-auto py-8 px-4">
        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Panneau d'administration</h2>
          <p className="text-gray-600">Gérez les contenus de votre site</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('actualites')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'actualites'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Newspaper className="w-5 h-5" />
            Actualités
          </button>
          <button
            onClick={() => setActiveTab('decouvrir')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'decouvrir'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Compass className="w-5 h-5" />
            Découvrir
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'products'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Package className="w-5 h-5" />
            Produits
          </button>
          <button
            data-testid="horaires-tab"
            onClick={() => setActiveTab('horaires')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'horaires'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <Clock className="w-5 h-5" />
            Horaires
          </button>
          <button
            data-testid="contacts-tab"
            onClick={() => setActiveTab('contacts')}
            className={`flex items-center gap-2 px-6 py-3 font-medium transition-all ${
              activeTab === 'contacts'
                ? 'text-green-700 border-b-2 border-green-700 bg-green-50'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Contacts
            {contacts.filter(c => !c.read).length > 0 && (
              <span className="bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                {contacts.filter(c => !c.read).length}
              </span>
            )}
          </button>
        </div>

        {/* Actualités Tab Content */}
        {activeTab === 'actualites' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="heading-lg text-gray-900 mb-2">Gestion des Actualités</h2>
                <p className="text-body text-gray-600">Gérez les actualités affichées sur la page d'accueil</p>
              </div>
              <button
                onClick={() => openModal()}
                className="btn btn-primary flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Nouvelle actualité
              </button>
            </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Chargement des actualités...</p>
          </div>
        ) : actualites.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Aucune actualité pour le moment</p>
            <button
              onClick={() => openModal()}
              className="btn btn-primary"
            >
              Créer votre première actualité
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {actualites.map((actualite) => {
              const badge = getCategoryBadge(actualite.category);
              return (
                <div key={actualite.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={actualite.image}
                      alt={actualite.title}
                      className="w-full h-full object-cover"
                    />
                    {actualite.isNew && (
                      <div className="absolute top-3 right-3 bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        Nouveau
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(actualite.date)}</span>
                    </div>
                    <h3 className="heading-sm text-gray-900 mb-2 line-clamp-2">{actualite.title}</h3>
                    <p className="text-body text-gray-600 line-clamp-3 mb-4">{actualite.description}</p>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.class}`}>
                        {badge.label}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setViewingItem(actualite);
                            setShowViewModal(true);
                          }}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Voir les détails"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                        </button>
                        <button
                          onClick={() => openModal(actualite)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Modifier"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(actualite.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Actualités Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="heading-lg text-gray-900">
                  {editingActualite ? 'Modifier l\'actualité' : 'Nouvelle actualité'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.title ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Nouveau produit disponible"
                  />
                  {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="4"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.description ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Décrivez votre actualité..."
                  />
                  {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                </div>

                {/* Lien cliquable dans le titre */}
                <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">🔗 Lien cliquable (optionnel)</h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL du lien
                    </label>
                    <input
                      type="url"
                      value={formData.link_url}
                      onChange={(e) => setFormData({ ...formData, link_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="https://example.com"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex: lien vers un article, une page, etc.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mot du titre à rendre cliquable
                    </label>
                    <input
                      type="text"
                      value={formData.link_text}
                      onChange={(e) => setFormData({ ...formData, link_text: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: panettoni"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Le mot sera affiché en <span className="text-blue-600 underline">bleu et souligné</span>
                    </p>
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image <span className="text-gray-400 text-xs">(optionnel si vidéo)</span>
                  </label>
                  
                  {/* File Upload Button */}
                  <div className="mb-3">
                    <label 
                      htmlFor="file-upload" 
                      className={`cursor-pointer inline-flex items-center gap-2 px-6 py-3 border-2 border-dashed rounded-lg transition-colors ${
                        uploading ? 'bg-gray-100 cursor-wait border-gray-300' : formData.image ? 'border-green-500 bg-green-50' : 'border-green-500 hover:bg-green-50'
                      }`}
                    >
                      <ImageIcon className={`w-5 h-5 ${formData.image && !uploading ? 'text-green-700' : 'text-green-600'}`} />
                      <span className={`text-sm font-medium ${formData.image && !uploading ? 'text-green-700' : 'text-gray-700'}`}>
                        {uploading ? '⏳ Upload en cours...' : formData.image ? '✅ Image chargée - Cliquer pour changer' : '📁 Choisir une image depuis le PC'}
                      </span>
                    </label>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileSelect}
                      disabled={uploading}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF ou WEBP (max 5MB)</p>
                  </div>

                  {/* URL Input (alternative) - Optional */}
                  {!formData.image && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-1 border-t border-gray-300"></div>
                        <span className="text-xs text-gray-500">OU</span>
                        <div className="flex-1 border-t border-gray-300"></div>
                      </div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Entrer une URL d'image externe
                      </label>
                      <input
                        type="url"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="https://example.com/image.jpg"
                        disabled={uploading}
                      />
                    </div>
                  )}
                  
                  {errors.media && !formData.image && !formData.video && (
                    <p className="text-red-500 text-sm mt-1">{errors.media}</p>
                  )}

                  {/* Image Preview */}
                  {formData.image && (
                    <div className="mt-3">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-xs font-medium text-green-700">✅ Image sélectionnée</p>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, image: '' })}
                          className="text-xs text-red-600 hover:text-red-700 underline"
                        >
                          Supprimer
                        </button>
                      </div>
                      <img
                        src={formData.image}
                        alt="Aperçu"
                        className="w-full h-48 object-cover rounded-lg shadow-sm border-2 border-green-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Galerie d'images (Upload multiple) */}
                <div className="bg-purple-50 p-4 rounded-lg">
                  <label className="block text-sm font-semibold text-purple-900 mb-3">
                    🖼️ Galerie d'images (optionnel - max 10)
                  </label>
                  <p className="text-xs text-gray-600 mb-3">Ajoutez plusieurs images pour créer un carrousel</p>
                  
                  <label 
                    htmlFor="multiple-files-upload" 
                    className={`cursor-pointer inline-flex items-center gap-2 px-6 py-3 border-2 border-dashed rounded-lg transition-colors ${
                      uploadingMultiple ? 'bg-gray-100 cursor-wait border-gray-300' : 'border-purple-500 hover:bg-purple-100'
                    }`}
                  >
                    <ImageIcon className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {uploadingMultiple ? '⏳ Upload en cours...' : formData.images.length > 0 ? `✅ ${formData.images.length} image(s) - Ajouter plus` : '📁 Choisir plusieurs images'}
                    </span>
                  </label>
                  <input
                    id="multiple-files-upload"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleMultipleFilesSelect}
                    disabled={uploadingMultiple}
                    multiple
                    className="hidden"
                  />

                  {/* Gallery Preview */}
                  {formData.images.length > 0 && (
                    <div className="mt-4 grid grid-cols-3 gap-3">
                      {formData.images.map((imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`Galerie ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border-2 border-purple-200"
                          />
                          <button
                            type="button"
                            onClick={() => removeGalleryImage(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Vidéo (optionnel) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Video className="w-4 h-4 inline mr-2" />
                    Vidéo (optionnel)
                  </label>
                  
                  {formData.video ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <video 
                          src={formData.video} 
                          controls 
                          className="w-full h-48 object-cover rounded-lg bg-black"
                        />
                        <button
                          type="button"
                          onClick={removeVideo}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-green-600">✓ Vidéo ajoutée</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          <input
                            type="file"
                            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                            onChange={handleVideoUpload}
                            className="hidden"
                            disabled={uploadingVideo}
                          />
                          <Video className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {uploadingVideo ? 'Upload en cours...' : 'Choisir une vidéo (max 100MB)'}
                          </span>
                        </label>
                      </div>
                      <div className="text-center text-gray-400 text-sm">ou</div>
                      <input
                        type="url"
                        value={formData.video}
                        onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                        placeholder="Coller l'URL d'une vidéo YouTube ou autre"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500">Formats acceptés: MP4, WebM, MOV, AVI, MPEG. Ou collez un lien YouTube.</p>
                    </div>
                  )}
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                      errors.date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="info">📢 Info</option>
                    <option value="nouveaute">✨ Nouveauté</option>
                    <option value="promotion">🎉 Promotion</option>
                  </select>
                </div>

                {/* IsNew Checkbox */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isNew"
                    checked={formData.isNew}
                    onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })}
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="isNew" className="text-sm font-medium text-gray-700">
                    Marquer comme "Nouveau" (badge orange)
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {editingActualite ? 'Mettre à jour' : 'Créer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal de visualisation plein écran */}
        {showViewModal && viewingItem && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                <h3 className="text-2xl font-bold text-gray-900">Détails de l'actualité</h3>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-8 h-8" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Image en grand */}
                {viewingItem.image && (
                  <div className="w-full rounded-lg overflow-hidden">
                    <img
                      src={viewingItem.image}
                      alt={viewingItem.title}
                      className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                    />
                  </div>
                )}

                {/* Titre */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">TITRE</h4>
                  <p className="text-2xl font-bold text-gray-900">{viewingItem.title}</p>
                </div>

                {/* Description complète */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">DESCRIPTION</h4>
                  <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {viewingItem.description}
                  </p>
                </div>

                {/* Informations supplémentaires */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">DATE</h4>
                    <div className="flex items-center gap-2 text-gray-900">
                      <Calendar className="w-5 h-5" />
                      <span className="text-lg">{formatDate(viewingItem.date)}</span>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">CATÉGORIE</h4>
                    <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getCategoryBadge(viewingItem.category).class}`}>
                      {getCategoryBadge(viewingItem.category).label}
                    </span>
                  </div>

                  {viewingItem.isNew && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-500 mb-2">STATUT</h4>
                      <span className="inline-block bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                        🆕 Nouveau
                      </span>
                    </div>
                  )}
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-3 pt-6 border-t border-gray-200">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      openModal(viewingItem);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Pencil className="w-5 h-5" />
                    Modifier
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setViewingItem(null);
                    }}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
          </div>
        )}

        {/* Horaires Tab Content */}
        {activeTab === 'horaires' && (
          <div className="max-w-2xl">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Horaires d'ouverture</h2>
              <p className="text-gray-600">Modifiez les horaires affichés sur le site (en-tête, page Infos pratiques).</p>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-4 gap-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <span>Jour</span>
                  <span>Ouvert</span>
                  <span>Ouverture</span>
                  <span>Fermeture</span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {hoursData.map((day, index) => (
                  <div key={day.day} className={`transition-colors ${day.isOpen ? 'bg-white' : 'bg-gray-50'}`}>
                    {/* Ligne principale */}
                    <div className="px-6 py-3 grid grid-cols-4 gap-4 items-center">
                      <span className="font-medium text-gray-800">{day.day}</span>
                      <div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            data-testid={`day-open-toggle-${index}`}
                            checked={day.isOpen}
                            onChange={(e) => updateDayHours(index, 'isOpen', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                          <span className={`ml-2 text-xs font-medium ${day.isOpen ? 'text-green-700' : 'text-red-500'}`}>
                            {day.isOpen ? 'Ouvert' : 'Fermé'}
                          </span>
                        </label>
                      </div>
                      <input
                        type="time"
                        data-testid={`day-time-open-${index}`}
                        value={day.open}
                        onChange={(e) => updateDayHours(index, 'open', e.target.value)}
                        disabled={!day.isOpen}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                      <input
                        type="time"
                        data-testid={`day-time-close-${index}`}
                        value={day.close}
                        onChange={(e) => updateDayHours(index, 'close', e.target.value)}
                        disabled={!day.isOpen}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-400 focus:border-transparent disabled:opacity-40 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Sous-ligne coupure (uniquement si jour ouvert) */}
                    {day.isOpen && (
                      <div className="px-6 pb-3 flex items-center gap-4 border-t border-dashed border-gray-100">
                        <div className="w-[calc(25%-0.5rem)]"></div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            data-testid={`day-break-toggle-${index}`}
                            checked={day.hasBreak || false}
                            onChange={(e) => updateDayHours(index, 'hasBreak', e.target.checked)}
                            className="w-3.5 h-3.5 rounded accent-orange-500"
                          />
                          <span className="text-xs text-gray-500 font-medium">Coupure midi</span>
                        </label>
                        {day.hasBreak && (
                          <div className="flex items-center gap-2">
                            <input
                              type="time"
                              data-testid={`day-break-start-${index}`}
                              value={day.breakStart || ''}
                              onChange={(e) => updateDayHours(index, 'breakStart', e.target.value)}
                              className="px-2 py-1 border border-orange-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-300 focus:border-transparent w-28"
                            />
                            <span className="text-gray-400 text-xs">–</span>
                            <input
                              type="time"
                              data-testid={`day-break-end-${index}`}
                              value={day.breakEnd || ''}
                              onChange={(e) => updateDayHours(index, 'breakEnd', e.target.value)}
                              className="px-2 py-1 border border-orange-300 rounded-lg text-xs focus:ring-2 focus:ring-orange-300 focus:border-transparent w-28"
                            />
                            <span className="text-xs text-orange-600 font-medium">
                              {day.breakStart && day.breakEnd
                                ? `${day.breakStart.replace(':00', 'h').replace(':30', 'h30')} – ${day.breakEnd.replace(':00', 'h').replace(':30', 'h30')}`
                                : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Section Bannière d'alerte */}
            <div className="mt-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Bannière d'alerte</h3>
              <p className="text-gray-600 text-sm mb-4">Affichez un message d'alerte sur toutes les pages du site (fermeture exceptionnelle, événement, etc.).</p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="alert-active-toggle"
                      checked={alertActive}
                      onChange={(e) => setAlertActive(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    <span className={`ml-3 text-sm font-medium ${alertActive ? 'text-amber-700' : 'text-gray-500'}`}>
                      {alertActive ? 'Bannière activée' : 'Bannière désactivée'}
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message d'alerte</label>
                  <input
                    type="text"
                    data-testid="alert-message-input"
                    value={alertMessage}
                    onChange={(e) => setAlertMessage(e.target.value)}
                    placeholder="Ex: Fermé les 24 et 25 décembre — Joyeuses fêtes !"
                    className="w-full px-4 py-2.5 border border-amber-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400 focus:border-transparent bg-white"
                  />
                </div>

                {alertActive && alertMessage && (
                  <div className="bg-amber-400 text-amber-900 py-2.5 px-4 rounded-lg text-sm font-medium flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                    Aperçu : {alertMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-4">
              <button
                data-testid="save-hours-btn"
                onClick={saveHours}
                disabled={savingHours}
                className="flex items-center gap-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-60"
              >
                {savingHours ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Enregistrer
                  </>
                )}
              </button>
              {hoursSaved && (
                <span className="flex items-center gap-1.5 text-green-600 font-medium text-sm">
                  <Check className="w-4 h-4" />
                  Paramètres sauvegardés !
                </span>
              )}
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Les horaires et la bannière d'alerte seront immédiatement visibles sur le site après sauvegarde.
              </p>
            </div>
          </div>
        )}

        {/* Contacts Tab Content */}
        {activeTab === 'contacts' && (
          <div>
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Messages reçus</h2>
                <p className="text-gray-600">
                  {contacts.filter(c => !c.read).length > 0
                    ? `${contacts.filter(c => !c.read).length} message(s) non lu(s)`
                    : 'Tous les messages ont été lus'}
                </p>
              </div>
              <button
                onClick={fetchContacts}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>
                Actualiser
              </button>
            </div>

            {loadingContacts ? (
              <div className="text-center py-12 text-gray-600">Chargement...</div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 text-gray-300 mx-auto mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                <p className="text-gray-500 text-lg">Aucun message reçu pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((contact) => (
                  <div
                    key={contact.id}
                    data-testid={`contact-message-${contact.id}`}
                    className={`bg-white rounded-xl border p-6 transition-all ${contact.read ? 'border-gray-200' : 'border-blue-400 shadow-sm shadow-blue-100'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          {!contact.read && (
                            <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">Non lu</span>
                          )}
                          <span className="font-bold text-gray-900">{contact.name}</span>
                          <span className="text-gray-500 text-sm">{contact.email}</span>
                          {contact.phone && <span className="text-gray-500 text-sm">{contact.phone}</span>}
                          <span className="text-gray-400 text-xs ml-auto">
                            {contact.created_at ? new Date(contact.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">{contact.subject}</p>
                        <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{contact.message}</p>
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        {!contact.read && (
                          <button
                            data-testid={`mark-read-btn-${contact.id}`}
                            onClick={() => markAsRead(contact.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                          >
                            <Check className="w-3.5 h-3.5" />
                            Marquer comme lu
                          </button>
                        )}
                        <a
                          data-testid={`reply-contact-btn-${contact.id}`}
                          href={`mailto:${contact.email}?subject=Re: ${encodeURIComponent(contact.subject)}&body=${encodeURIComponent(`Bonjour ${contact.name},\n\n`)}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7"></polyline><path d="M20 18v-2a4 4 0 0 0-4-4H4"></path></svg>
                          Répondre
                        </a>
                        <button
                          data-testid={`delete-contact-btn-${contact.id}`}
                          onClick={() => deleteContact(contact.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-medium transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Découvrir Tab Content */}
        {activeTab === 'decouvrir' && (
          <DecouvrirAdmin />
        )}

        {/* Products Tab Content */}
        {activeTab === 'products' && (
          <ProductsAdmin />
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default Admin;
